import { Injectable } from '@angular/core';
import { DomSanitizer, SafeStyle, Title } from '@angular/platform-browser';
import { Params, Router } from '@angular/router';
import { currencyMap, languages, pagination, params, platforms } from '@app/configurations';
import { LocalizedDate } from '@app/core/utilities/date.utilities';
import { microsToNumber, numberToMicros } from '@app/core/utilities/number.utilities';
import { getNextSortStep, splitCompositeSubFilter } from '@app/core/utilities/refiners.utilities';
import { snakeToCamelCase } from '@app/core/utilities/string.utilities';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Observer, of } from 'rxjs';
import { AdPlatform } from '../models/common.models';
import { AdspertApps, MainMenuLink } from '../models/main-menu.model';
import {
  CONVERSION_RATE,
  CONVERSION_RATE_ADJUSTED,
  CONVERSIONS,
  CONVERSIONS_ADJUSTED,
  COST,
  COST_FOR_CLICK,
  COST_FOR_PROFIT,
  COST_FOR_REVENUE,
  COST_PER_CONVERSION,
  COST_PER_CONVERSION_ADJUSTED,
  COST_REVENUE_RATIO,
  COST_REVENUE_RATIO_ADJUSTED,
  currencyMetrics,
  percentMetrics,
  PG_GOAL_METRIC_COST_PER_CLICK,
  PG_GOAL_METRIC_COST_PER_CONVERSION,
  PG_GOAL_METRIC_COST_PER_DAY,
  PG_GOAL_METRIC_CRR,
  PG_GOAL_METRIC_PROFIT_MAXIMIZATION,
  PG_GOAL_METRIC_ROAS,
  PG_GOAL_METRIC_ROI,
  PROFIT_GROSS,
  PROFIT_GROSS_ADJUSTED,
  PROFIT_NET,
  PROFIT_NET_ADJUSTED,
  RETURN_ON_INVESTMENT,
  RETURN_ON_INVESTMENT_ADJUSTED,
  REVENUE,
  REVENUE_ADJUSTED,
  REVENUE_ORGANIC,
  REVENUE_ORGANIC_ADJUSTED,
} from '../models/metrics.constants';
import { GlobalFilterDefaults } from '../store/states/global-filters/global-filters-state.models';
import { generateExplorerHref, getQueryParameter } from '../utilities/url.utilities';
import { isGermanLocale, RemoveDelaySuffix } from './metrics-calculations.service';

export const weekdays = {
  [languages.ENGLISH]: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  [languages.GERMAN]: ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'],
  [languages.CHINESE]: ['周日', '周一', '周二', '周三', '周四', '周五', '周六'],
};
export const months = {
  [languages.ENGLISH]: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  [languages.GERMAN]: [
    'Januar',
    'Februar',
    'März',
    'April',
    'Mai',
    'Juni',
    'Juli',
    'August',
    'September',
    'Oktober',
    'November',
    'Dezember',
  ],
  [languages.CHINESE]: [
    '一月',
    '二月',
    '三月',
    '四月',
    '五月',
    '六月',
    '七月',
    '八月',
    '九月',
    '十月',
    '十一月',
    '十二月',
  ],
};
export const shortMonths = {
  [languages.ENGLISH]: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  [languages.GERMAN]: ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'],
  [languages.CHINESE]: months[languages.CHINESE],
};

export const shortWeekdays = {
  [languages.ENGLISH]: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
  [languages.GERMAN]: ['So.', 'Mo.', 'Di.', 'Mi.', 'Do.', 'Fr.', 'Sa.'],
  [languages.CHINESE]: weekdays[languages.CHINESE],
};

export const getCurrencyFromSymbolOrValue = (symbolOrValue) => {
  let val;
  if (currencyMap[symbolOrValue]) return symbolOrValue;

  Object.keys(currencyMap).forEach((k) => {
    if (currencyMap[k].symbol === symbolOrValue) val = k;
  });
  return val;
};

export const IsOauthApp = () => location.pathname.startsWith('/oauth/account');
export const IsBilledApp = () => location.pathname.startsWith('/billed-usage-report');
export const IsSignupApp = () => window.__IS_SIGNUP_APP;
export const IsPortalApp = () => window.__IS_PORTAL_APP;

export const AppHasAuth = () => !(IsSignupApp() || IsPortalApp() || IsBilledApp());

export const getCookieByKey = (key: string): string | null => {
  key += '=';
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i += 1) {
      const cookie = cookies[i].trim();
      if (cookie.startsWith(key)) {
        cookieValue = decodeURIComponent(cookie.replace(key, ''));
        break;
      }
    }
  }
  return cookieValue;
};

export function getUniqueItemsBy<T extends object>(arr: T[], key): T[] {
  return [...new Map(arr.map((item) => [item[key], item])).values()];
}

export function RemoveLoader() {
  let elem = document.getElementById('app-loading');
  elem?.parentNode?.removeChild(elem);
  elem = null;
}

export function SetBrowserAgentClass() {
  const global = window as any;

  // Firefox 1.0+
  const isFirefox = typeof global.InstallTrigger !== 'undefined';

  // Safari 3.0+
  const isSafari = navigator.userAgent.indexOf('Safari') !== -1 && navigator.userAgent.indexOf('Chrome') === -1;

  // Internet Explorer 6-11
  const isIE = !!window['MSInputMethodContext'] && !!document['documentMode'];

  // Edge 20+
  const isEdge = /Edge\/\d./i.test(navigator.userAgent);

  // Chrome 1+
  const isChrome = !!global.chrome;

  if (isFirefox) {
    document.body.classList.add('firefox');
  }
  if (isSafari) {
    document.body.classList.add('safari');
  }
  if (isIE && !isEdge) {
    document.body.classList.add('ie');
  }
  if (isEdge) {
    document.body.classList.add('edge');
  }
  if (isChrome) {
    document.body.classList.add('chrome');
  }
}
export function SetOSClass() {
  const userAgent = window.navigator.userAgent;
  const platform = window.navigator.platform;
  const macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'];
  const windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'];
  const iosPlatforms = ['iPhone', 'iPad', 'iPod'];
  let os = null;
  if (macosPlatforms.indexOf(platform) !== -1) {
    os = 'mac';
  } else if (iosPlatforms.indexOf(platform) !== -1) {
    os = 'ios';
  } else if (windowsPlatforms.indexOf(platform) !== -1) {
    os = 'windows';
  } else if (/Android/.test(userAgent)) {
    os = 'android';
  } else if (!os && /Linux/.test(platform)) {
    os = 'linux';
  }
  return document.body.classList.add(os);
}

/* eslint-disable no-bitwise */
/*
 * [hi-base32]{@link https://github.com/emn178/hi-base32}
 *
 * @version 0.5.0
 * @author Chen, Yi-Cyuan [emn178@gmail.com]
 * @copyright Chen, Yi-Cyuan 2015-2018
 * @license MIT
 */
export const encode32 = (str) => {
  const BASE32_ENCODE_CHAR = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567'.split('');
  const blocks = [0, 0, 0, 0, 0, 0, 0, 0];
  if (typeof str !== 'string') {
    return null;
  } else {
    let v1;
    let v2;
    let v3;
    let v4;
    let v5;
    let code;
    let end = false;
    let base32Str = '';
    let index = 0;
    let i;
    let start = 0;
    let bytes = 0;
    const length = str.length;
    do {
      blocks[0] = blocks[5];
      blocks[1] = blocks[6];
      blocks[2] = blocks[7];
      for (i = start; index < length && i < 5; ++index) {
        code = str.charCodeAt(index);
        if (code < 0x80) {
          blocks[i++] = code;
        } else if (code < 0x800) {
          blocks[i++] = 0xc0 | (code >> 6);
          blocks[i++] = 0x80 | (code & 0x3f);
        } else if (code < 0xd800 || code >= 0xe000) {
          blocks[i++] = 0xe0 | (code >> 12);
          blocks[i++] = 0x80 | ((code >> 6) & 0x3f);
          blocks[i++] = 0x80 | (code & 0x3f);
        } else {
          code = 0x10000 + (((code & 0x3ff) << 10) | (str.charCodeAt(++index) & 0x3ff));
          blocks[i++] = 0xf0 | (code >> 18);
          blocks[i++] = 0x80 | ((code >> 12) & 0x3f);
          blocks[i++] = 0x80 | ((code >> 6) & 0x3f);
          blocks[i++] = 0x80 | (code & 0x3f);
        }
      }
      bytes += i - start;
      start = i - 5;
      if (index === length) {
        ++index;
      }
      if (index > length && i < 6) {
        end = true;
      }
      v1 = blocks[0];
      if (i > 4) {
        v2 = blocks[1];
        v3 = blocks[2];
        v4 = blocks[3];
        v5 = blocks[4];
        base32Str +=
          BASE32_ENCODE_CHAR[v1 >>> 3] +
          BASE32_ENCODE_CHAR[((v1 << 2) | (v2 >>> 6)) & 31] +
          BASE32_ENCODE_CHAR[(v2 >>> 1) & 31] +
          BASE32_ENCODE_CHAR[((v2 << 4) | (v3 >>> 4)) & 31] +
          BASE32_ENCODE_CHAR[((v3 << 1) | (v4 >>> 7)) & 31] +
          BASE32_ENCODE_CHAR[(v4 >>> 2) & 31] +
          BASE32_ENCODE_CHAR[((v4 << 3) | (v5 >>> 5)) & 31] +
          BASE32_ENCODE_CHAR[v5 & 31];
      } else if (i === 1) {
        base32Str += BASE32_ENCODE_CHAR[v1 >>> 3] + BASE32_ENCODE_CHAR[(v1 << 2) & 31] + '======';
      } else if (i === 2) {
        v2 = blocks[1];
        base32Str +=
          BASE32_ENCODE_CHAR[v1 >>> 3] +
          BASE32_ENCODE_CHAR[((v1 << 2) | (v2 >>> 6)) & 31] +
          BASE32_ENCODE_CHAR[(v2 >>> 1) & 31] +
          BASE32_ENCODE_CHAR[(v2 << 4) & 31] +
          '====';
      } else if (i === 3) {
        v2 = blocks[1];
        v3 = blocks[2];
        base32Str +=
          BASE32_ENCODE_CHAR[v1 >>> 3] +
          BASE32_ENCODE_CHAR[((v1 << 2) | (v2 >>> 6)) & 31] +
          BASE32_ENCODE_CHAR[(v2 >>> 1) & 31] +
          BASE32_ENCODE_CHAR[((v2 << 4) | (v3 >>> 4)) & 31] +
          BASE32_ENCODE_CHAR[(v3 << 1) & 31] +
          '===';
      } else {
        v2 = blocks[1];
        v3 = blocks[2];
        v4 = blocks[3];
        base32Str +=
          BASE32_ENCODE_CHAR[v1 >>> 3] +
          BASE32_ENCODE_CHAR[((v1 << 2) | (v2 >>> 6)) & 31] +
          BASE32_ENCODE_CHAR[(v2 >>> 1) & 31] +
          BASE32_ENCODE_CHAR[((v2 << 4) | (v3 >>> 4)) & 31] +
          BASE32_ENCODE_CHAR[((v3 << 1) | (v4 >>> 7)) & 31] +
          BASE32_ENCODE_CHAR[(v4 >>> 2) & 31] +
          BASE32_ENCODE_CHAR[(v4 << 3) & 31] +
          '=';
      }
    } while (!end);
    return base32Str;
  }
};
/* eslint-enable no-bitwise */

export const findReplaceString = (val, find, replace) => {
  if (/[a-zA-Z\_]+/g.test(val)) {
    return val.replace(new RegExp('{{(?:\\s+)?(' + find + ')(?:\\s+)?}}'), replace);
  } else {
    throw new Error('Find statement does not match regular expression: /[a-zA-Z_]+/');
  }
};
export const mapMetricsToGoals = (b) => {
  return {
    [PG_GOAL_METRIC_COST_PER_DAY]: microsToNumber(b.cost),
    [PG_GOAL_METRIC_COST_PER_CLICK]: microsToNumber(b.costPerClick),
    [PG_GOAL_METRIC_COST_PER_CONVERSION]: microsToNumber(b.costPerConversion),
    [PG_GOAL_METRIC_CRR]: b.costRevenueRatio,
    [PG_GOAL_METRIC_ROI]: b.returnOnInvestment,
    [PG_GOAL_METRIC_ROAS]: b.returnOnAdSpend,
    [PG_GOAL_METRIC_PROFIT_MAXIMIZATION]:
      b.profitNet === undefined ? microsToNumber(b.profit) : microsToNumber(b.profitNet),
  };
};

export const updateToEqualityFilterRefiners = (refiners: [string, string][]) => {
  const updatedRefiners: [string, string][] = [] as any;
  refiners.forEach(([key, value]: [string, string]) => {
    if (key.match(/filter_.*_eq/i)) {
      // equality filter
      updatedRefiners.push([key.replace('_eq', '_ge'), value]);
      updatedRefiners.push([key.replace('_eq', '_le'), value]);
    } else if (key.match(/filter_.*_range/i)) {
      // range filter
      updatedRefiners.push([key.replace('_range', '_ge'), value.split('^^')[0]]);
      updatedRefiners.push([key.replace('_range', '_le'), value.split('^^')[1]]);
    } else updatedRefiners.push([key, value]);
  });
  return updatedRefiners;
};

export const getMetricValueForAPI = (key, value, ignorePercentMetric = false) => {
  if (percentMetrics.includes(key)) {
    if (ignorePercentMetric) value = parseFloat(value).toString();
    else value = (parseFloat(value) / 100).toString();
  } else if (currencyMetrics.includes(key)) value = numberToMicros(parseFloat(value)).toString();
  return { key, value };
};

export function lowerCaseFirstLetter(string) {
  return string.charAt(0).toLowerCase() + string.slice(1);
}

export function campaignsCountbyPlatform(data: { platform: AdPlatform }[] = []) {
  const campaigns = {};

  Object.keys(platforms).forEach((p) => (campaigns[p] = 0));
  data.forEach((campaign) => campaigns[campaign.platform]++);

  Object.keys(campaigns).forEach((key) => {
    if (campaigns[key] === 0 && key !== 'all') delete campaigns[key];
  });

  return {
    platforms: Object.keys(platforms).filter((p) => !!campaigns[p]).length,
    all: data.length,
    ...campaigns,
  };
}

export function getLocaleFormattedNumber(value) {
  if (!value) return null;
  value = value.toString();
  return parseFloat(isGermanLocale() ? value.split('.').join('').replace(',', '.') : value.split(',').join(''));
}

export function setUrlParameters(item: MainMenuLink, state: any): MainMenuLink {
  switch (item.app) {
    case AdspertApps.Apollo:
      item.url = item.path;
      item.queryParams = getParams(state, item);
      return item;
    case AdspertApps.Explorer:
      item.url = generateExplorerHref(item.path, getParams(state, item, true));
      return item;
  }
}

function getParams(state: any, item: MainMenuLink, forExplorer = false) {
  const tmpParams: Params = {};
  item.params.forEach(({ key }) => {
    if (!!state[key]) tmpParams[key] = state[key];
  });
  if (forExplorer) {
    if (item.path === 'admin/scopes') {
      tmpParams.scopeType = item.key === 'accounts' ? 'ACCOUNT' : 'CUSTOMER';
    }
    return tmpParams;
  }

  if (!item.hasDestinationFilters) {
    tmpParams[params.GLOBAL_FILTER_DEST_PERFORMANCE_GROUP_QUERY] = null;
    tmpParams[params.GLOBAL_FILTER_DEST_PERFORMANCE_GROUP_ID] = null;
    tmpParams[params.GLOBAL_FILTER_DEST_CAMPAIGN_ID] = null;
    tmpParams[params.GLOBAL_FILTER_DEST_CAMPAIGN_QUERY] = null;
    tmpParams[params.GLOBAL_FILTER_DEST_AD_GROUPS_ID] = null;
    tmpParams[params.GLOBAL_FILTER_DEST_AD_GROUPS_QUERY] = null;
  }
  if (item.key === 'products') {
    // remove this when products api supports x-accnt
    if (
      !!tmpParams[params.GLOBAL_FILTER_AD_GROUPS_ID] &&
      (!tmpParams[params.GLOBAL_FILTER_CAMPAIGN_ID] || !tmpParams[params.ADSPERT_ID])
    ) {
      delete tmpParams[params.GLOBAL_FILTER_AD_GROUPS_ID];
    }
  }

  if (item.key === 'goals') {
    tmpParams[params.GLOBAL_FILTER_AD_GROUPS_ID] = null;
    tmpParams[params.GLOBAL_FILTER_AD_GROUPS_QUERY] = null;
  }
  return tmpParams;
}

@Injectable()
export class UtilitiesService {
  constructor(
    private router: Router,
    private titleService: Title,
    private sanitizer: DomSanitizer,
    private translate: TranslateService,
  ) {}

  public setToSessionStorage(key, data) {
    sessionStorage.setItem(`adspertstore.${key}`, JSON.stringify(data));
  }
  public getFromSessionStorage(key) {
    const data = sessionStorage.getItem(`adspertstore.${key}`);
    if (data) {
      try {
        return JSON.parse(data);
      } catch (error) {
        return undefined;
      }
    } else {
      return undefined;
    }
  }
  public clearSessionStorage() {
    let n = sessionStorage.length;
    while (n--) {
      const key = sessionStorage.key(n);
      if (/adspertstore/.test(key)) {
        sessionStorage.removeItem(key);
      }
    }
  }
  public logout(): void {
    this.clearSessionStorage();
    location.href = `${location.origin}/portal/logout`;
  }

  public redirectConnectAppToCurrentState(
    { data, status, failure },
    currentPath: string,
    isCustomerSignup = true,
  ): boolean {
    let tmp = false;
    if (!!failure) {
      // check for errors and navigate to specific page based on error code
      switch (failure) {
        case environment.connectApp.errCodes.ACCOUNT_EXISTS:
          tmp = this._redirect('/account-exists', currentPath, data?.customer_id);
          break;
        case environment.connectApp.errCodes.NO_ACCOUNTS:
          tmp = this._redirect('/account-not-found', currentPath, data?.customer_id);
          break;
        case environment.connectApp.errCodes.INSUFFICIENT_PERMISSIONS:
          tmp = this._redirect('/insufficient-permission', currentPath, data?.customer_id);
          break;
        case environment.connectApp.errCodes.OAUTH_MS_WORK_ACCOUNT:
          tmp = this._redirect('/insufficient-permission', currentPath, data?.customer_id);
          break;
        case environment.connectApp.errCodes.OAUTH_REJECTED:
          tmp = this._redirect('/auth-error', currentPath, data?.customer_id);
          break;
        default:
          tmp = this._redirect('/error', currentPath, data?.customer_id);
          break;
      }
    } else {
      // if no errors, navigate user to a specific page based on status code
      switch (status) {
        case environment.connectApp.status.CREATE_USER: // FE status, this is returned by frontend service
          tmp = this._redirect('/start', currentPath, data?.customer_id);
          break;
        case environment.connectApp.status.ERROR:
          tmp = this._redirect('/error', currentPath, data?.customer_id);
          break;
        case environment.connectApp.status.TERMS_CONSENT:
          tmp = this._redirect('/start-auth0', currentPath, data?.customer_id);
          break;
        case environment.connectApp.status.OAUTH_CONSENT:
          tmp = this._redirect('/connect-account', currentPath, data?.customer_id);
          break;
        case environment.connectApp.status.ACCOUNT_LIST_RETRIEVAL:
          tmp = this._redirect('/account-selection', currentPath, data?.customer_id);
          break;
        case environment.connectApp.status.ACCOUNT_SELECTION:
          tmp = this._redirect('/account-selection', currentPath, data?.customer_id);
          break;
        case environment.connectApp.status.CUSTOMER_SETUP:
          tmp = this._redirect('/processing', currentPath, data?.customer_id);
          break;
        case environment.connectApp.status.ACCOUNT_SYNC:
          if (currentPath === '/processing') {
            window.__SIGNUP_IGNORE_GUARD = true; // set this to bypass route guard
            tmp = this._redirect('/success', currentPath, data?.customer_id);
          } else {
            if (isCustomerSignup) {
              tmp = this._redirect('/welcome', currentPath, data?.customer_id);
            } else {
              tmp = this._redirect('/success', currentPath, data?.customer_id);
            }
          }
          break;
        case environment.connectApp.status.NONE:
          if (isCustomerSignup) {
            tmp = this._redirect('/welcome', currentPath, data?.customer_id);
            // everything is ready; redirect to welcome for user to decide what's next
          } else {
            // if user ends up in account signup page and state is NONE
            // reset things so that user can add new account
            // location.replace(location.origin + '/signup/reset');
            tmp = this._redirect('/success', currentPath, data?.customer_id);
          }
          break;
        default:
          break;
      }
    }
    return tmp;
  }
  private _redirect(newPath: string, currentPath: string, customerId?: number): boolean {
    const currentRoute = currentPath && currentPath.split('?')[0];
    // check and redirect to new route
    if (currentRoute !== newPath) {
      const queryParams = {};
      try {
        currentPath
          .split('?')[1]
          .split('&')
          .forEach((d) => {
            queryParams[d.split('=')[0]] = d.split('=')[1];
          });
      } catch (error) {}
      const platform = getQueryParameter('account');
      if (!!platform) queryParams['account'] = platform;
      queryParams[params.CUSTOMER_ID] = queryParams[params.CUSTOMER_ID] || customerId;
      this.router.navigate([newPath], { queryParams });
    }
    return currentRoute === newPath;
  }
  public formatDate(value: Date | string | number, format) {
    return LocalizedDate(value, format);
  }
  public santizeMarkup(markup: string): SafeStyle {
    return this.sanitizer.bypassSecurityTrustStyle(markup);
  }
  public getAmazonProductImage(uid: string): SafeStyle | null {
    if (!uid) {
      return null;
    }
    return this.sanitizer.bypassSecurityTrustStyle(
      `url(${uid.replace('http://ecx.images-amazon.com', 'https://d1ge0kk1l5kms0.cloudfront.net')}),url(${uid})`,
    );
  }
  public noAccess() {
    this.router.navigate(['no-access'], {
      queryParamsHandling: 'preserve',
      replaceUrl: true,
    });
  }
  public toImpersonate(): string {
    return getQueryParameter(params.IMPERSONATION_ID) as string;
  }

  public loadScript(id: string, src: string): Observable<boolean> {
    if (typeof window[id] === 'undefined') {
      const data$: Observable<boolean> = new Observable((observer: Observer<boolean>) => {
        const script: any = document.createElement('script');
        script.src = src;
        script.type = 'text/javascript';
        if (script.readyState) {
          // IE
          script.onreadystatechange = () => {
            if (script.readyState === 'loaded' || script.readyState === 'complete') {
              script.onreadystatechange = null;
              setTimeout(() => {
                observer.next(true);
                observer.complete();
              });
            }
          };
        } else {
          script.onload = () => {
            observer.next(false);
            observer.complete();
          };
        }
        document.head.appendChild(script);
      });
      return data$;
    } else {
      return of(true);
      // lib already registered
    }
  }

  public setTitle(pageName: string): void {
    if (!pageName) {
      return;
    }

    pageName = this.translate.instant(pageName);

    this.titleService.setTitle(`${pageName} - ${environment.app.name}`);
  }

  public refinersToUrl(data: any[]): string {
    return data.map((d) => d.join('=')).join('&');
  }
  public removeEmptyStateOnTableSort(currentSort: { key: string; value: string }, previousSortValue: string) {
    // current table sorting is 3 steps ascend, descend, reset
    // as the table sorting config doesnt provide a way to configure this.
    // using the below function to have only 2 states descend & ascend
    if (!currentSort.value) {
      currentSort.value = previousSortValue === 'ascend' ? 'descend' : 'ascend';
    }
    return currentSort;
  }
  public descendAsDefaultTableSort(currentSort: { key: string; value: string }) {
    // current table sorting is 3 steps ascend, descend, reset
    // as the table sorting config doesnt provide a way to configure this.
    // using the below function to have descend as default sort type
    currentSort.value = getNextSortStep(currentSort.value);

    return currentSort;
  }
  public getPageSizeOptions(total): number[] {
    const tmp = [];
    for (let i = 0; i < pagination.sizeOptions.length; i++) {
      tmp.push(pagination.sizeOptions[i]);
      if (total <= pagination.sizeOptions[i]) {
        break;
      }
    }
    return tmp;
  }
  public isTourEnabled(): boolean {
    return !!document.querySelector('adspt-header .wizard');
  }
  public hasData(data): boolean {
    let hasData = false;
    data.forEach(({ clicks, impressions, conversions, cost, revenue }) => {
      if (clicks !== 0 || impressions !== 0 || conversions !== 0 || cost !== 0 || revenue !== 0) {
        hasData = true;
      }
    });
    return hasData;
  }
  public getURIFromCurrentState(state: any, defaultFilter: GlobalFilterDefaults, includeAdGroup = true) {
    const uri = [];
    if (typeof state[params.ADSPERT_ID] !== 'string' && typeof state[params.GLOBAL_FILTER_ACCOUNT_QUERY] !== 'string') {
      uri.push(...this.extractAccountRefiners(defaultFilter.accountsDefaultFilter));
    } else {
      if (state[params.ADSPERT_ID]) {
        uri.push(['filter_adspert_account_id', state[params.ADSPERT_ID]]);
      } else if (state[params.GLOBAL_FILTER_ACCOUNT_QUERY]) {
        uri.push(...this.extractAccountRefiners(state[params.GLOBAL_FILTER_ACCOUNT_QUERY]));
      }
    }
    if (state[params.GLOBAL_FILTER_PERFORMANCE_GROUP_ID]) {
      uri.push(['filter_adspert_performance_group_id', state[params.GLOBAL_FILTER_PERFORMANCE_GROUP_ID]]);
    } // add else condition when pg subsets are added
    if (
      typeof state[params.GLOBAL_FILTER_CAMPAIGN_ID] !== 'string' &&
      typeof state[params.GLOBAL_FILTER_CAMPAIGN_QUERY] !== 'string'
    ) {
      // use default values
      uri.push(...this.extractCampaignsRefiners(defaultFilter.campaignsDefaultFilter));
    } else {
      if (state[params.GLOBAL_FILTER_CAMPAIGN_ID]) {
        uri.push(['filter_adspert_campaign_id', state[params.GLOBAL_FILTER_CAMPAIGN_ID]]);
      } else if (state[params.GLOBAL_FILTER_CAMPAIGN_QUERY]) {
        uri.push(...this.extractCampaignsRefiners(state[params.GLOBAL_FILTER_CAMPAIGN_QUERY]));
      }
    }

    if (includeAdGroup) {
      if (
        typeof state[params.GLOBAL_FILTER_AD_GROUPS_ID] !== 'string' &&
        typeof state[params.GLOBAL_FILTER_AD_GROUPS_QUERY] !== 'string'
      ) {
        // use default values
        uri.push(...this.extractAdgroupsRefiners(defaultFilter.adGroupsDefaultFilter));
      } else {
        if (state[params.GLOBAL_FILTER_AD_GROUPS_ID]) {
          uri.push(['filter_adspert_adgroup_id', state[params.GLOBAL_FILTER_AD_GROUPS_ID]]);
        } else if (state[params.GLOBAL_FILTER_AD_GROUPS_QUERY]) {
          uri.push(...this.extractAdgroupsRefiners(state[params.GLOBAL_FILTER_AD_GROUPS_QUERY]));
        }
      }

      if (
        typeof state[params.GLOBAL_FILTER_CRITERIA_ID] !== 'string' &&
        typeof state[params.GLOBAL_FILTER_CRITERIA_QUERY] !== 'string'
      ) {
        // use default values
        uri.push(...this.extractCriteriaRefiners(defaultFilter.criteriaDefaultFilter));
      } else {
        if (state[params.GLOBAL_FILTER_CRITERIA_ID]) {
          uri.push(['filter_adspert_criterion_id', state[params.GLOBAL_FILTER_CRITERIA_ID]]);
        } else if (state[params.GLOBAL_FILTER_CRITERIA_QUERY]) {
          uri.push(...this.extractCriteriaRefiners(state[params.GLOBAL_FILTER_CRITERIA_QUERY]));
        }
      }
    }

    return uri;
  }
  public extractAccountRefiners(data, forAPIMutator?: boolean): Record<string, string>[] {
    const uri = [];
    data
      .split(';')
      .filter((d) => !d.toUpperCase().endsWith(':ALL'))
      .forEach((param) => {
        const [key, value] = splitCompositeSubFilter(param);
        switch (key.toLowerCase()) {
          case 'platform':
            uri.push(['filter_account_platform', value]);
            break;
          case 'optimize':
            uri.push(['filter_account_optimize', value]);
            break;
          case 'tags':
            uri.push(['filter_account_tags_user', value]);
            break;
        }
      });
    if (forAPIMutator) {
      const filters = {};
      uri.forEach((d) => (filters[snakeToCamelCase(d[0].replace('filter_', ''))] = d[1]));
      return filters as any;
    }
    return uri;
  }
  public extractCampaignsRefiners(data) {
    const uri = [];
    data
      .split(';')
      .filter((d) => !d.toUpperCase().endsWith(':ALL'))
      .forEach((param) => {
        const [key, value] = splitCompositeSubFilter(param);
        switch (key) {
          case 'status':
            uri.push(['filter_campaign_status', value]);
            break;
          case 'optimize':
            uri.push(['filter_campaign_optimize', value]);
            break;
          case 'ras':
            uri.push(['filter_campaign_amz_ras_campaign', value]);
            break;
          case 'optiType':
            uri.push(['filter_campaign_optimization_type', value]);
            break;
          case 'type':
            uri.push(['filter_campaign_type', value]);
            break;
          case 'name':
            uri.push(['search_campaign_name', value]);
            break;
          case 'portfolio':
            uri.push(['filter_adspert_portfolio_id', value]);
            break;
          case 'label':
            uri.push(['filter_', value]);
            break;
        }
      });
    return uri;
  }
  public extractAdgroupsRefiners(data) {
    const uri = [];
    data
      .split(';')
      .filter((d) => !d.toUpperCase().endsWith(':ALL'))
      .forEach((param) => {
        const [key, value] = splitCompositeSubFilter(param);
        switch (key) {
          case 'status':
            uri.push(['filter_adgroup_status', value]);
            break;
          case 'name':
            uri.push(['search_adgroup_name', value]);
            break;
        }
      });
    return uri;
  }
  public extractCriteriaRefiners(data) {
    const uri = [];
    data
      .split(';')
      .filter((d) => !d.toUpperCase().endsWith(':ALL'))
      .forEach((param) => {
        const [key, value] = splitCompositeSubFilter(param);
        switch (key) {
          case 'status':
            uri.push(['filter_criterion_status', value]);
            break;
          case 'matchType':
            uri.push(['filter_criterion_match_type', value]);
            break;
          case 'type':
            uri.push(['filter_criterion_type', value]);
            break;
          case 'name':
            uri.push(['search_criterion_name', value]);
            break;
        }
      });
    return uri;
  }
  public splitProductPartition(criterionName) {
    const re = /(?:> )?([^:]+): (?:(?:\"([^"]+)\" ?)|([^>]+$)|(?:([^>]+) ))/gm;
    if (criterionName === 'All Products') {
      return [['All Products', null]];
    }
    const parts = [];

    Array.from(criterionName.matchAll(re)).forEach((m) => {
      parts.push([m[1], m[2] || m[3] || m[4]]);
    });
    return parts;
  }
  public getDelayAdjustedMetric(delay, e: any) {
    if (e === COST_FOR_CLICK || e === COST_FOR_PROFIT || e === COST_FOR_REVENUE) {
      e = COST;
    }
    if (!delay) {
      e = RemoveDelaySuffix(e);
    } else {
      if (e === CONVERSIONS) {
        e = CONVERSIONS_ADJUSTED;
      } else if (e === PROFIT_GROSS) {
        e = PROFIT_GROSS_ADJUSTED;
      } else if (e === PROFIT_NET) {
        e = PROFIT_NET_ADJUSTED;
      } else if (e === REVENUE) {
        e = REVENUE_ADJUSTED;
      } else if (e === REVENUE_ORGANIC) {
        e = REVENUE_ORGANIC_ADJUSTED;
      } else if (e === COST_PER_CONVERSION) {
        e = COST_PER_CONVERSION_ADJUSTED;
      } else if (e === CONVERSION_RATE) {
        e = CONVERSION_RATE_ADJUSTED;
      } else if (e === RETURN_ON_INVESTMENT) {
        e = RETURN_ON_INVESTMENT_ADJUSTED;
      } else if (e === COST_REVENUE_RATIO) {
        e = COST_REVENUE_RATIO_ADJUSTED;
      }
    }
    return e;
  }
}
